/**
 * UX for the the ZObject which is a max-msp inspired object
 * providing a generic interface for writing functions for
 * messages
 */

import {
    savePreset,
} from 'components/v2/trackRightClickActions.js';
import {useContextMenu} from "components/dropdowns/useContextMenu.js";
import {useMintToHic} from 'components/hooks/useMintToHic.js';
import {useMintToZora} from 'components/hooks/useMintToZora.js';
import React, { useRef, useEffect, useCallback, useState} from 'react';
import ContextMenu from "components/dropdowns/ContextMenu.js";
import MenuItem from "components/dropdowns/MenuItem.js";
import SubMenu from "components/dropdowns/SubMenu.js";
import {
    setZObjectsStack,
    setZObjectBackground,
    setPluginToSave,
    setUXLockObject,
    setWindowPosition,
    setZObjectInputText,
    setFullScreenZObject,
    setSelectedZObject,
    setZObjectContextMenu,
    setSelectedPulledElements} from 'actions/index.js';
import {useDoubleClick} from "components/hooks/useDoubleClick.js";
import {NW, NE, SW, SE} from 'components/windows/resize-types.js';
import Possibilities from "components/effects/scripting/Possibilities.js";
import {connect} from 'react-redux';
import {useWindowManager} from "components/windows/useWindowManager.js";
import MessageInlets from "components/messaging/MessageInlets.js";
import {setViewableSubPatches} from 'actions/index.js';
import MessageOutlets from "components/messaging/MessageOutlets.js";
import {useSyntaxHelper} from "components/effects/scripting/useSyntaxHelper.js"
import {getCustomZObject} from './getCustomZObject.js';
import {doc} from 'zobject/definitions/docs.js';
import ZObjectPositioning from './ZObjectPositioning.js';
import {TRACK} from 'constants/object-types.js';


import '../../zobject.scss';

const ZObjectComponent = ({
    setZObjectContextMenu,
    setZObjectsStack,
    presentationMode,
    isInstrumentView,
    isBackground,
    setZObjectBackground,
    forceShow,
    setPluginToSave,
    zobject,
    setLocked,
    flashTimestamp,
    isLocked,
    coords,
    isMainNFT,
    isStatic,
    setSelectedZObject,
    sequencerEngine,
    fillColor,
    customMode,
    isSelectedZObject,
    scale,
    planeMapping,
    sizeRef,
    containerRef,
    elements,
    setOrdering,
    isSelected,
    ordering,
    size,
    setSize,
    setViewableSubPatches,
    setSelectedPulledElements,
    setFullScreenZObject,
    includeInPresentation,
    hidden,
    setZObjectInputText
}) => { 
    
    const ref = useRef(undefined);
    const inputRef = useRef(undefined);

    const mouseRef = useRef({});

    const onContextMenu = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        let rect = containerRef.current.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;
        setZObjectContextMenu(zobject.subSequence.id, {x,y}, zobject);
    }, [zobject]);

    const [editing, setEditing] = useState(false);
    const [editingDown, setEditingDown] = useState(false);

    let [text, setText] = useState(zobject._text || "");
    let [isMapped, setIsMapped] = useState(false);
    let [overflow, setOverflow] = useState(false);

    const openSubPatch = useCallback(() => {
        let _zobject = zobject;
        if (zobject.pluginViewerFor) {
            _zobject = zobject.pluginViewerFor;
        }
        let spatches = _zobject.sequencerEngine.state.zobject.viewableSubPatches;
        if (_zobject.subSequence && _zobject.subSequence.zObject) {
            _zobject.effect.width = _zobject.subSequence.width;
            _zobject.effect.height = _zobject.subSequence.height;
            
            let windowPosition = zobject.sequencerEngine.state.windows.windowPositions[
                zobject.subSequence.id];
            zobject.sequencerEngine.dispatch(setWindowPosition(
                _zobject.effect.id,
                windowPosition));
        }
        setViewableSubPatches(
            [... spatches, _zobject.effect]);
    }, []);

    let customRef = useRef(customMode);
    useEffect(() => {
        customRef.current = customMode;
    }, [customMode]);

    const doubleClickCallback = useCallback((e) => {
        e.stopPropagation();
        if (zobject.name === "subpatch" && !zobject.effect.isCustom) {
            openSubPatch();
        } else {
            let obj = {};
            obj[zobject.id] = true;
            setSelectedPulledElements(obj);
        }
        setSelectedZObject(zobject);
    }, [customMode]);

    let [flashing, setFlashing] = useState(false);
    let flashCount = useRef(0);
    useEffect(() => {
        flashCount.current++;
        let count = flashCount.current;

        if (flashTimestamp && new Date().getTime() - flashTimestamp < 5000) {
            setFlashing(true);
            setTimeout(() => {
                if (flashCount.current === count) {
                    setFlashing(false);
                }
            }, 4000);
        }
    }, [flashTimestamp, setFlashing]);

    const selectObject = useCallback((e) => {
        e.stopPropagation();
        if (!isSelectedZObject) {
            if (zobject.state.spatial.lockedObjects[zobject.id] ||
                zobject.state.spatial.lockedObjects[zobject.effect && zobject.effect.id]) {
                return;
            }
            if (zobject.effect && zobject.effect.getObjectType &&
                zobject.effect.getObjectType() === TRACK) {
                setSelectedZObject(zobject.effect);
            } else {
                if (zobject.name === 'cirklon') {
                    setSelectedZObject(zobject.sequencerEngine.getSelectedTrack());
                } else {
                    setSelectedZObject(zobject);
                }
            }
        }
    }, [isSelectedZObject]);
    
    const onSingleClick = useCallback((e) => {
        
    }, [zobject]);

    const onDoubleClick = useDoubleClick(doubleClickCallback, 250, onSingleClick);

    const onClick = useCallback(() => {
        setIsMapped(true);
        planeMapping.randomlyMapParameters(zobject.effect);
    }, [setIsMapped, planeMapping]);

    useEffect(() => {
        if (!zobject._text && inputRef.current) {
            setEditing(true);
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (text !== zobject._text) {
            setText(zobject._text);
        }
    }, [zobject._text, text, setText]);

    const onTextChange = useCallback((e) => {
        let _text = e.target.value;
        zobject.text = _text;
        setText(_text);
        setZObjectInputText(_text);
    }, [setText]);

    let {possibilities, selectedChoice, choosePossibility, setPossibilities} = useSyntaxHelper({
        code: text,
        skipMouse: true,
        ref: inputRef,
        timeout: 40,
        onChange: onTextChange,
        doc: doc
    });

    const {
        zIndex,
        setWindowScale,
        isDragging,
        onMouseDown,
        onMouseUp,
        setCoords,
        startResizing,
        windowScale
    } = useWindowManager({
        scale,
        ref,
        sequencerEngine,
        sizeRef,
        containerRef,
        size,
        setSize,
        elements: elements,
        element: zobject,
        setOrdering,
        ordering,
    });

    let style = {
        zIndex: zIndex
    };

    const removeFromScreen = useCallback(() => {
        let fs = zobject.state.zobject.fullScreenZObject;
        if (!Array.isArray(fs)) {
            fs = [fs];
        }
        setFullScreenZObject(fs.filter(x => x !== zobject));
    }, [zobject]);
    useEffect(() => {
        if (zobject.effect && zobject.width) {
            setWindowScale({
                x: zobject.width,
                y: zobject.height
            });
        }
    }, [zobject.effect, setWindowScale, zobject.width, zobject.height]);

    let _style = {... style};

    let textStyle = {};
    if (text.length > 0) {
        let _width = text.length+1;
        textStyle.width = (text.length + 1) + "ch";

        let numInlets = zobject.numInlets;
        if (zobject.effect && zobject.effect.numInlets) {
            numInlets += zobject.effect.numInlets;
        }

        if (numInlets*4 > _width) {
            _width = numInlets*4;
            textStyle.width = numInlets*4 + "ch";
        }

        let numOutlets = zobject.numOutlets;
        if (zobject.effect && zobject.effect.numOutlets) {
            numOutlets += zobject.effect.numOutlets;
        }


        if (numOutlets * 4 > _width) {
            textStyle.width = numOutlets*4 + "ch";
        }
    }

    _style = {
        ... _style,
        ... textStyle
    };

    let className = "z-object";
    let isResizing = false;
    if (isSelected && (customMode !== "custom" || !zobject.subSequence.zObject)) {
        className += " resizable";
        isResizing = true;
    }

    if (flashing) {
        className += " flashing";
    }
    if (overflow) {
        className += " overflow";
    }

    let showRemoveFromScreen = false;
    if (isSelectedZObject) {
        className += " selected";
        if (Array.isArray(zobject.state.zobject.fullScreenZObject)) {
            if (zobject.state.zobject.fullScreenZObject.includes(zobject)) {
                showRemoveFromScreen = true;
            }
        }
    }

    let custom = getCustomZObject(zobject, windowScale && windowScale.x, windowScale && windowScale.y, containerRef, setOverflow);
    if (custom) {
        className += " custom";
        delete _style.width ;
    }

    if (isMainNFT) {
        className += " main-nft";
    }

    if (custom && zobject.effect && zobject.effect.zObjects && !zobject.effect.isCustom) {
        className += " subpatch-zobject";
    }

    if (custom && zobject.effect && zobject.effect.isCustom) {
        className += " custom-subpatch-container";
    }

    if (zobject.name === "comment") {
        className += " comment";
    }

    if (zobject.name === "script") {
        className += " script";
    }

    if (zobject.name === "track" && zobject.attributes["show-sequencer"]) {
        className += " showing-sequencer";
    }

    if (zobject.hasInletFields) {
        className += " has-inlet-fields";
    }

    if (zobject.name === "pad") {
        className += " z-object-pad";
    }

    if (zobject.name === "sampleBank") {
        className += " z-sample-bank";
    }

    if (zobject.name === "icon") {
        className += " z-icon";
    }

    if (zobject.name === "cirklon") {
        className += " zcirklon";
        if (zobject.attributes["secondary-color"]) {
            _style.backgroundColor = zobject.attributes["secondary-color"];
        }
    }

    if (zobject.name === "ztracker") {
        className += " ztracker-object";
    }

    if (zobject.name === "patterns") {
        className += " zpatterns";
    }

    if (windowScale && windowScale.x > 0 && windowScale.y  > 0) {
        if (custom) {
            if (zobject.name === "subpatch" && !zobject.effect.isCustom) {
            } else {
                _style.width = windowScale.x;
                _style.height = windowScale.y;
            }
        }
    }

    if (zobject.attributes["fill subpatch"]) {
        let subpatch = zobject.subSequence;
        if (subpatch.zObject) {
            let {width, height} = subpatch.zObject;
            _style.width = width;
            _style.height = height;
        }
    }

    if (isBackground) {
        _style.width = window.innerWidth - 240;
        _style.height = window.innerHeight - 80;;
    }

    if (planeMapping && zobject.effect) {
        if (!isMapped) {
            className += " plane-mapping";
        }
    }

    if (zobject.effect && zobject.effect.zObjects && zobject.effect.isCustom) {
        if (zobject.attributes) {
            _style.backgroundColor = zobject.attributes["primary-color"];
            if (zobject.attributes["secondary-color"]) {
                _style.borderColor = zobject.attributes["secondary-color"];
            }
        }
        className += " custom-fill";
    }


    if (zobject.attributes["primary-color"]) {
        _style.backgroundColor = zobject.attributes["primary-color"];
    }

    if (zobject.name === "macro") {
        _style.backgroundColor = undefined;
    }

    _style.borderRadius = zobject.attributes["border-radius"] + 'px';

    useEffect(() => {
        window.addEventListener("click", onWindowClick);
        return () => window.addEventListener("click", onWindowClick);
    }, [setEditing]);

    const includeObjectInPresentation = useCallback(() => {
        let include = zobject.attributes["Include in Presentation"] === true;
        zobject.setAttribute("Include in Presentation", !include);
    }, [zobject]);

    const onWindowClick = useCallback(() => {
        setEditing(false);
    }, [setEditing]);

    const toggleLock = useCallback(() => {
        setLocked(!isLocked);
    }, [isLocked]);

    const _onMouseDown = useCallback((e) => {
        e.stopPropagation();
        if (!zobject.effect) {
            selectObject(e);
        }
        /*
        mouseRef.current.pageX = e.pageX;
        mouseRef.current.pageY = e.pageY;
        */
        onMouseDown(e);
    }, [isSelectedZObject]);

    useEffect(() => {
        if (isSelectedZObject) {
            let stack = zobject.state.zobject.zobjectsStack.filter(
                x => x !== zobject);
            stack.push(zobject);
            setZObjectsStack(stack);
        }
    }, [isSelectedZObject]);

    let windows = zobject.state.windows;
    let position = presentationMode ? windows.presentationWindowPositions[zobject.id] ||
        windows.windowPositions[zobject.id] : windows.windowPositions[zobject.id];
    if (position) {
        _style.left = position.x;
        _style.top = position.y;
    }
    if (customMode === "custom") {
        let [w,h] = [zobject.subSequence.zObject.width, zobject.subSequence.zObject.height];
        if (position && (position.x > w || position.y > h)) {
            return '';
        }
    }

    if (zobject.name === "track") {
        className += " z-track";
    }

    if (hidden && !forceShow) {
        return '';
    }

    let innerCustomStyle = {};
    if (zobject.attributes["border-radius"]) {
        innerCustomStyle["borderRadius"] = zobject.attributes["border-radius"] - 3 + "px";
    }

    if (!editing) {
        className += " not-editing";
    }

    if (isBackground) {
        className += " is-background";
    }

    if (includeInPresentation) {
        className += " include-in-presentation";
    }

    return (<div
              onMouseDown={
                  isMainNFT ||
                      isStatic ||
                      overflow ||
                      customMode === "custom" ?
                      (e) => (isMainNFT ? e.stopPropagation() : 0) :
                      _onMouseDown
              }
              onContextMenu={e => {
                  // if we are "inside" a sub-patch  then we dont show the context menu
                  if (zobject.subSequence.inletObjects) {
                      let spatches = zobject.sequencerEngine.state.zobject.viewableSubPatches;
                      if (!spatches.includes(zobject.subSequence)) {
                          return;
                      }
                  }
                  e.stopPropagation();
                  onContextMenu(e);
              }}
              onClick={(e) => {
                  let fs = zobject.state.zobject.fullScreenZObject;
                  if (!fs) {
                      fs = [];
                  } else if (!Array.isArray(fs)) {
                      fs = [fs];
                  }
                  let fslen = fs.length;
                  if (isMainNFT && zobject.name !== "three.canvas" && fslen <= 1) {
                      e.stopPropagation() ;
                  } else {
                      if (planeMapping) {
                          onClick(e);
                      } else {
                          if (customMode === "custom" && zobject.subSequence.zObject) {
                              return;
                          }
                          onDoubleClick(e);
                      }
                  }
              }}
                  
              onMouseUp={(e) => {
                  /*
                  if (e.pageX === mouseRef.current.pageX &&
                      e.pageY === mouseRef.current.pageY) {
                      selectObject(e);
                  }
                  */
                  onMouseUp(e);
              }}
              ref={ref}
              style={_style} className={className}>
              {showRemoveFromScreen &&
               <div onClick={removeFromScreen} className="pushable remove-from-fullscreen">
                 Remove From Screen
               </div>}
              <div style={innerCustomStyle} className="inner-custom">{custom}</div>
              <div className={custom ? "hide-basic" : "basic"}>
                <input
                  onMouseDown={(e) => {
                      let positions = zobject.sequencerEngine.state.windows.windowPositions;
                      let coords = positions[zobject.id];
                      if (editing) {
                          e.stopPropagation();
                      }
                      setEditingDown(coords);
                  }}
                  onClick={(e) => {
                      let positions = zobject.sequencerEngine.state.windows.windowPositions;
                      let coords = positions[zobject.id];
                      e.stopPropagation();
                      if (coords === editingDown) {
                          setEditing(true);
                      }
                  }}
                  spellcheck="false"
                  style={textStyle}
                  ref={inputRef}
                  readOnly={!editing}
                  type="text"
                  className={editing ? "z-object-input editing" : "z-object-input no-select"}
                  onChange={onTextChange}
                  value={text}/>
                <ZObjectPositioning isInstrumentView={isInstrumentView} windowRef={ref} zobject={zobject}/>
                <Possibilities
                  doc={doc}
                  selectedChoice={selectedChoice}
                  choosePossibility={choosePossibility}
                  scrollTop={0}
                  input={inputRef}
                  setPossibilities={setPossibilities}
                     possibilities={possibilities}/>
              </div>
              <MessageInlets doc={doc} numInlets={zobject.numInlets} effect={zobject}/>
              {!overflow && <MessageOutlets
                numOutlets={zobject.numOutlets}
                subSequence={zobject.subSequence}
                containerRef={containerRef} scale={scale}
                              effect={zobject}/>}
            {isResizing && custom && [
                          <div
            onMouseDown={(e) => startResizing(NW, e)}
                            className="top-left-edge"/>,
          <div
            onMouseDown={(e) => startResizing(NE, e)}
            className="top-right-edge"/>,
          <div
            onMouseDown={(e) => startResizing(SW, e)}
            className="bottom-left-edge"/>,
          <div
            onMouseDown={(e) => startResizing(SE, e)}
            className="bottom-right-edge"/>,
 
            ]}
            </div>);
};

const mapStateToProps = (state, props) => {
    let presentationMode = state.spatial.presentationMode || props.isInstrumentView;
    let hidden = state.spatial.hiddenObjects[props.zobject.id];
    if (presentationMode && !props.zobject.attributes["Include in Presentation"]) {
        hidden = true;
    }
    return {
        presentationMode,
        includeInPresentation: !presentationMode && props.zobject.attributes["Include in Presentation"],
      outerWidth: props.zobject.subSequence.zObject && props.zobject.subSequence.zObject.width,
      outerHeight: props.zobject.subSequence.zObject && props.zobject.subSequence.zObject.height,
      function: props.zobject.function,
      name: props.zobject.name,
      isSelected: state.windows.selectedElements[props.zobject.id],
      isSelectedZObject: state.zobject.selectedZObject === props.zobject ||
          (state.zobject.selectedZObject && props.zobject.effect === state.zobject.selectedZObject),
      width: props.zobject.width,
      height: props.zobject.height,
      fillColor: props.zobject.effect && props.zobject.effect.fillColor,
        hidden,
      styling: state.zobject.objectStylings[props.zobject.id],
      flashTimestamp: state.zobject.zobjectsFlashing[props.zobject.id],
      isLocked: state.spatial.lockedObjects[props.zobject.id],
      isBackground: state.zobject.zobjectBackground && state.zobject.zobjectBackground.id === props.zobject.id
     
  };
};

const mapDispatchToProps = (dispatch, props) => {
  return {
      setViewableSubPatches: x => dispatch(setViewableSubPatches(x)),
      setSelectedPulledElements: x => dispatch(setSelectedPulledElements(x)),
      setSelectedZObject : x => dispatch(setSelectedZObject(x)),
      setFullScreenZObject: x => dispatch(setFullScreenZObject(x)),
      setZObjectInputText: x => dispatch(setZObjectInputText(x)),
      setLocked: x => dispatch(setUXLockObject(props.zobject.id, x)),
      setPluginToSave: x => dispatch(setPluginToSave(x)),
      setZObjectBackground: x=> dispatch(setZObjectBackground(x)),
      setZObjectsStack: x=> dispatch(setZObjectsStack(x)),
      setZObjectContextMenu: (id, position, zobject) => dispatch(setZObjectContextMenu(id, position, zobject)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ZObjectComponent)
